import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Download,
  ArrowDownToLine,
  ArrowRight,
  Database,
  Globe2,
  ShieldCheck,
  AlertTriangle,
  BarChart3,
  Microscope,
  Cpu,
  ScrollText,
  Scale,
  Fingerprint,
  Network,
  Boxes,
  Map,
  FileText,
  Search,
  Eye,
  TrendingUp,
  Sparkles,
  ExternalLink,
  Linkedin,
} from "lucide-react";

const PAPER_PDF =
  "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/j614vrv2_Emailing%20lesNouvelles_June2026_Online_Article_09%20%282%29.pdf";

const LES_LOGO =
  "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/oxft8qvj_LES.png";

const AUTHOR_PHOTO =
  "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/sr0xp2cf_ata1730458078594.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

// ------------------------------------------------------------------
// Reusable decorative SKU / metadata grid (futuristic matrix background)
// ------------------------------------------------------------------
function MatrixGrid({ opacity = 0.18, density = 12 }) {
  return (
    <svg
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity }}
      preserveAspectRatio="none"
      viewBox="0 0 1200 800"
    >
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#22D3EE" strokeWidth="0.4" />
        </pattern>
        <radialGradient id="vignette" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(2,6,23,0)" />
          <stop offset="100%" stopColor="rgba(2,6,23,0.85)" />
        </radialGradient>
      </defs>
      <rect width="1200" height="800" fill="url(#grid)" />
      <rect width="1200" height="800" fill="url(#vignette)" />
      {Array.from({ length: density }).map((_, i) => {
        const x = (i * 97) % 1200;
        const y = (i * 173) % 800;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="2" fill="#22D3EE" opacity="0.8">
              <animate
                attributeName="opacity"
                values="0.2;1;0.2"
                dur={`${2 + (i % 4)}s`}
                repeatCount="indefinite"
                begin={`${(i % 5) * 0.3}s`}
              />
            </circle>
            <line
              x1={x}
              y1={y}
              x2={(x + 80) % 1200}
              y2={(y + 60) % 800}
              stroke="#0041E6"
              strokeWidth="0.5"
              opacity="0.5"
            />
          </g>
        );
      })}
    </svg>
  );
}

// ------------------------------------------------------------------
// Hero badge pill
// ------------------------------------------------------------------
function HeroBadge({ children, accent = "#22D3EE" }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.18em] uppercase px-3.5 py-1.5 rounded-full border backdrop-blur-md"
      style={{
        background: "rgba(8, 12, 35, 0.55)",
        borderColor: `${accent}55`,
        color: "#E6F1FF",
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
      />
      {children}
    </span>
  );
}

// ------------------------------------------------------------------
// Section header
// ------------------------------------------------------------------
function SectionHeader({ kicker, title, intro, kickerColor = "#22D3EE" }) {
  return (
    <motion.div {...fadeUp} className="max-w-3xl">
      <div
        className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.32em] uppercase mb-5"
        style={{ color: kickerColor }}
      >
        <span className="inline-block w-6 h-px" style={{ background: kickerColor }} />
        {kicker}
      </div>
      <h2
        className="font-display text-white leading-[0.96]"
        style={{ fontSize: "clamp(32px, 4.8vw, 64px)" }}
      >
        {title}
      </h2>
      {intro && (
        <p className="mt-6 text-[16px] md:text-[18px] text-[#A6B5D6] leading-relaxed">
          {intro}
        </p>
      )}
    </motion.div>
  );
}

// ------------------------------------------------------------------
// Animated stat counter
// ------------------------------------------------------------------
function StatCard({ value, suffix, label, icon: Icon, accent = "#22D3EE", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-2xl p-7 overflow-hidden group"
      style={{
        background:
          "linear-gradient(160deg, rgba(13, 22, 60, 0.85) 0%, rgba(2, 6, 23, 0.95) 100%)",
        border: `1px solid ${accent}33`,
        boxShadow: `0 1px 0 ${accent}22 inset, 0 30px 60px -25px rgba(0, 0, 0, 0.5)`,
      }}
    >
      <div
        className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at 50% 0%, ${accent}33, transparent 40%)`,
        }}
      />
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background: `${accent}1F`,
            border: `1px solid ${accent}55`,
            color: accent,
          }}
        >
          <Icon size={18} strokeWidth={2.2} />
        </div>
      </div>
      <div
        className="font-display text-white leading-none"
        style={{ fontSize: "clamp(40px, 5vw, 64px)" }}
      >
        {value}
        <span className="ml-1" style={{ color: accent }}>
          {suffix}
        </span>
      </div>
      <div className="mt-3 text-[14px] md:text-[15px] text-[#A6B5D6] leading-snug">{label}</div>
    </motion.div>
  );
}

// ------------------------------------------------------------------
// Gap card (metadata gap list)
// ------------------------------------------------------------------
function GapCard({ icon: Icon, title, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="relative rounded-xl p-5 flex items-start gap-4"
      style={{
        background: "rgba(13, 22, 60, 0.55)",
        border: "1px solid rgba(34, 211, 238, 0.18)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: "rgba(34, 211, 238, 0.10)",
          border: "1px solid rgba(34, 211, 238, 0.35)",
          color: "#22D3EE",
        }}
      >
        <Icon size={18} strokeWidth={2.2} />
      </div>
      <div>
        <div className="font-semibold text-white text-[15px] leading-snug">{title}</div>
      </div>
    </motion.div>
  );
}

// ------------------------------------------------------------------
// Benefit card (For licensors)
// ------------------------------------------------------------------
function BenefitCard({ icon: Icon, title, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="relative rounded-xl px-5 py-4 flex items-center gap-3"
      style={{
        background:
          "linear-gradient(180deg, rgba(0, 65, 230, 0.10) 0%, rgba(13, 22, 60, 0.55) 100%)",
        border: "1px solid rgba(0, 65, 230, 0.35)",
      }}
    >
      <div
        className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(0, 65, 230, 0.20)", color: "#7AB2FF" }}
      >
        <Icon size={16} strokeWidth={2.4} />
      </div>
      <div className="text-white text-[14px] font-semibold leading-snug">{title}</div>
    </motion.div>
  );
}

// ------------------------------------------------------------------
// Pipeline step (Section 6)
// ------------------------------------------------------------------
function PipelineStep({ label, icon: Icon, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex flex-col items-center text-center"
    >
      <div
        className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center"
        style={{
          background:
            "linear-gradient(160deg, rgba(34, 211, 238, 0.15) 0%, rgba(0, 65, 230, 0.25) 100%)",
          border: "1px solid rgba(34, 211, 238, 0.45)",
          boxShadow:
            "0 0 32px rgba(34, 211, 238, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
          color: "#7DF9FF",
        }}
      >
        <motion.div
          className="absolute inset-0 rounded-2xl"
          animate={{ boxShadow: ["0 0 0 rgba(34,211,238,0)", "0 0 24px rgba(34,211,238,0.4)", "0 0 0 rgba(34,211,238,0)"] }}
          transition={{ duration: 2.4, repeat: Infinity, delay: idx * 0.3 }}
        />
        <Icon size={26} strokeWidth={1.8} />
      </div>
      <div
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-[11px] font-black flex items-center justify-center"
        style={{
          background: "#22D3EE",
          color: "#031023",
          boxShadow: "0 0 12px rgba(34,211,238,0.6)",
        }}
      >
        {idx + 1}
      </div>
      <div className="mt-3 text-white text-[12px] md:text-[13px] font-semibold leading-tight max-w-[110px]">
        {label}
      </div>
    </motion.div>
  );
}

// ==================================================================
// MAIN PAGE
// ==================================================================
export default function ResearchPage() {
  // SEO — set title + meta tags on mount
  useEffect(() => {
    document.title = "eGeez Research | Is Licensing Really a $369.9B Industry?";

    const meta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    meta(
      "description",
      "A published research paper by Nathen Mazri on why the global licensing industry needs census-level SKU metadata, AI-powered licensing infrastructure, royalty transparency, and marketplace verification.",
    );
    meta("og:title", "Is Licensing Really a $369.9B Industry?", "property");
    meta(
      "og:description",
      "Nathen Mazri's les Nouvelles research paper argues that licensing needs a census-level metadata system, not fragmented surveys.",
      "property",
    );
    meta("og:type", "article", "property");
    meta("og:url", window.location.href, "property");

    // scroll to top on mount
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  return (
    <div
      className="relative min-h-screen text-white overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at top, #0B1538 0%, #060B22 35%, #02050F 75%)",
      }}
      data-testid="research-page"
    >
      {/* Global ambient grid */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <MatrixGrid opacity={0.22} density={18} />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(800px circle at 80% 10%, rgba(0,65,230,0.18), transparent 50%), radial-gradient(600px circle at 10% 60%, rgba(34,211,238,0.10), transparent 50%)",
          }}
        />
      </div>

      {/* ============= HERO ============= */}
      <section
        className="relative pt-20 md:pt-28 pb-20 md:pb-28"
        data-testid="research-hero"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12">
          <motion.div
            {...fadeUp}
            className="flex items-center gap-3 mb-6"
          >
            <img
              src={LES_LOGO}
              alt="Licensing Executives Society International"
              className="h-12 md:h-14 w-auto rounded-md ring-1 ring-white/15"
              draggable={false}
              data-testid="les-logo"
            />
            <div className="text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase text-[#7AB2FF]">
              les Nouvelles · June 2026
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-white leading-[0.94] max-w-[1100px]"
            style={{
              fontSize: "clamp(36px, 6.2vw, 88px)",
              textShadow: "0 6px 30px rgba(0, 0, 0, 0.4)",
            }}
            data-testid="research-headline"
          >
            Is Licensing Really a{" "}
            <span style={{ color: "#22D3EE" }}>$369.9 Billion</span> Industry?
            <br />
            <span className="italic text-[#7AB2FF]">
              Then Why Is It Still Measured by Surveys?
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 text-[17px] md:text-[20px] text-[#C9D5F2] leading-relaxed max-w-3xl"
          >
            A published practitioner–scholar research paper by{" "}
            <span className="text-white font-semibold">Nathen Mazri</span> argues that
            the global licensing industry needs a census-level, SKU-level metadata
            system — not fragmented reports, delayed surveys, manual royalty files, and
            disconnected marketplace data.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-4 text-[14px] md:text-[15px] text-[#7AB2FF] italic"
          >
            Published in <em>les Nouvelles</em>, June 2026 issue — by the Licensing
            Executives Society International.
          </motion.p>

          {/* Badges */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-2.5"
            data-testid="hero-badges"
          >
            <HeroBadge accent="#22D3EE">Published Research</HeroBadge>
            <HeroBadge accent="#7AB2FF">les Nouvelles — June 2026</HeroBadge>
            <HeroBadge accent="#FFD400">40,000+ Licensed SKUs Studied</HeroBadge>
            <HeroBadge accent="#22D3EE">AI-Powered Licensing Infrastructure</HeroBadge>
            <HeroBadge accent="#7AB2FF">eGeez Research</HeroBadge>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={PAPER_PDF}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full font-bold text-[15px] md:text-[16px] transition shadow-[0_18px_40px_-12px_rgba(34,211,238,0.55)]"
              style={{
                background: "linear-gradient(135deg, #22D3EE 0%, #0041E6 100%)",
                color: "#031023",
              }}
              data-testid="hero-download-btn"
            >
              <ArrowDownToLine size={18} strokeWidth={2.5} />
              Download Research Paper
            </a>

            <a
              href="#key-findings"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold text-[15px] md:text-[16px] border transition hover:bg-white/5"
              style={{
                borderColor: "rgba(122, 178, 255, 0.5)",
                color: "#C9D5F2",
              }}
              data-testid="hero-explore-btn"
            >
              Explore Key Findings
              <ArrowRight size={16} strokeWidth={2.4} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ============= SECTION 1 — The Question ============= */}
      <section
        id="the-question"
        className="relative py-24 md:py-32"
        data-testid="section-the-question"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <SectionHeader
              kicker="01 · The Question"
              title="If Licensing Is Worth $369.9B, Where Is the Census?"
            />
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-8 text-[16px] md:text-[18px] text-[#C9D5F2] leading-relaxed"
            >
              The licensing industry is measured through annual surveys and fragmented
              reporting — yet SKU-level product reality lives scattered across
              marketplaces, retailers, licensees, approval portals, royalty PDFs, and
              isolated territory databases.
            </motion.p>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-4 text-[16px] md:text-[18px] text-[#C9D5F2] leading-relaxed"
            >
              This paper argues that licensing needs a shared intelligence layer — a
              backbone in the spirit of{" "}
              <span className="text-white font-semibold">ISBN</span> for books,{" "}
              <span className="text-white font-semibold">ISRC</span> for music,{" "}
              <span className="text-white font-semibold">DOI</span> for research, and{" "}
              <span className="text-white font-semibold">GS1</span> for global commerce.
              Without it, the industry remains opaque, vulnerable, and uncountable.
            </motion.p>
          </div>

          {/* Right — comparison standards visual */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 space-y-3"
          >
            <div className="text-[10px] font-black tracking-[0.32em] uppercase text-[#22D3EE] mb-3">
              ★ Industry Standards Inspiration
            </div>
            {[
              { k: "ISBN", v: "Universal book identifier · 200+ countries" },
              { k: "ISRC", v: "Sound recording fingerprint · IFPI managed" },
              { k: "DOI", v: "Persistent identifier for research outputs" },
              { k: "GS1", v: "Global commerce data — barcodes & GTIN" },
              { k: "eGeez SKU?", v: "Proposed: licensed-product census layer", highlight: true },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * i, duration: 0.5 }}
                className="rounded-xl px-5 py-4 flex items-center justify-between"
                style={{
                  background: row.highlight
                    ? "linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(0,65,230,0.25) 100%)"
                    : "rgba(13, 22, 60, 0.5)",
                  border: row.highlight
                    ? "1px solid rgba(34, 211, 238, 0.55)"
                    : "1px solid rgba(122, 178, 255, 0.18)",
                  boxShadow: row.highlight
                    ? "0 0 24px rgba(34,211,238,0.2)"
                    : "none",
                }}
              >
                <div className="font-display text-white text-[22px] md:text-[26px]">
                  {row.k}
                </div>
                <div className="text-right text-[12px] md:text-[13px] text-[#A6B5D6] max-w-[60%] leading-snug">
                  {row.v}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============= SECTION 2 — The Metadata Gap ============= */}
      <section
        id="metadata-gap"
        className="relative py-24 md:py-32"
        data-testid="section-metadata-gap"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12">
          <SectionHeader
            kicker="02 · The Metadata Gap"
            title="The Industry Has Rights. But It Lacks a Shared Metadata Backbone."
            kickerColor="#FFD400"
          />

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-10 rounded-2xl p-6 md:p-8"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,212,0,0.10) 0%, rgba(13,22,60,0.6) 100%)",
              border: "1px solid rgba(255, 212, 0, 0.35)",
            }}
          >
            <div className="flex items-start gap-4">
              <AlertTriangle
                size={28}
                strokeWidth={2}
                className="text-[#FFD400] flex-shrink-0"
              />
              <p className="text-[16px] md:text-[18px] text-white leading-relaxed">
                Licensed products do{" "}
                <span className="text-[#FFD400] font-bold">not</span> have a universal
                SKU-level intelligence system connecting IP owners, licensees,
                retailers, marketplaces, customs, consumers, and royalty reporting.
              </p>
            </div>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { i: Fingerprint, t: "No universal licensed SKU identifier" },
              { i: ScrollText, t: "Fragmented approval portals" },
              { i: FileText, t: "Manual royalty reporting" },
              { i: Search, t: "Weak marketplace verification" },
              { i: Map, t: "Inconsistent territory visibility" },
              { i: ShieldCheck, t: "No unified authenticity layer" },
            ].map((c, i) => (
              <GapCard key={i} icon={c.i} title={c.t} delay={0.05 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 3 — What the Research Studied ============= */}
      <section
        id="research-scope"
        className="relative py-24 md:py-32"
        data-testid="section-research-scope"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              kicker="03 · Scope"
              title="A Three-Year Practitioner–Scholar Study"
              intro="Combining academic rigor with operational fieldwork — across territories, marketplaces, licensees, and licensed product lifecycles."
            />
          </div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {[
              { i: Boxes, t: "40,000+ licensed SKUs examined" },
              { i: Globe2, t: "Hundreds of global licensees mapped" },
              { i: Eye, t: "Fan & volunteer SKU sightings" },
              { i: Search, t: "Mystery online shopping" },
              { i: BarChart3, t: "Marketplace monitoring" },
              { i: Microscope, t: "Manual verification across regions" },
              { i: Map, t: "Territory leakage simulation" },
              { i: AlertTriangle, t: "Counterfeit & anomaly analysis" },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * i, duration: 0.5 }}
                className="flex items-center gap-3 rounded-xl px-5 py-4"
                style={{
                  background: "rgba(13, 22, 60, 0.5)",
                  border: "1px solid rgba(122, 178, 255, 0.22)",
                }}
              >
                <div
                  className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{
                    background: "rgba(34, 211, 238, 0.10)",
                    border: "1px solid rgba(34, 211, 238, 0.30)",
                    color: "#22D3EE",
                  }}
                >
                  <row.i size={16} strokeWidth={2.2} />
                </div>
                <span className="text-[14px] md:text-[15px] text-white">{row.t}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============= SECTION 4 — Key Findings ============= */}
      <section
        id="key-findings"
        className="relative py-24 md:py-32"
        data-testid="section-key-findings"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12">
          <SectionHeader
            kicker="04 · Key Findings"
            title="What the Research Revealed"
            kickerColor="#FF6FA5"
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            <StatCard
              value="22"
              suffix="%"
              icon={FileText}
              accent="#22D3EE"
              label="of listings showed licensee information"
              delay={0.0}
            />
            <StatCard
              value="34"
              suffix="%"
              icon={Map}
              accent="#FFD400"
              label="of SKUs appeared in unauthorized territories"
              delay={0.1}
            />
            <StatCard
              value="15"
              suffix="%"
              icon={AlertTriangle}
              accent="#FF6FA5"
              label="appeared in three or more unauthorized territories"
              delay={0.2}
            />
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { i: AlertTriangle, t: "Counterfeits increase where metadata is weak", a: "#FF6FA5" },
              { i: TrendingUp, t: "Royalty tracking suffers when SKUs are not standardized", a: "#22D3EE" },
              { i: Globe2, t: "Territorial leakage is structural — not occasional", a: "#FFD400" },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 * i }}
                className="rounded-2xl p-6 flex items-start gap-3"
                style={{
                  background: "rgba(13, 22, 60, 0.6)",
                  border: `1px solid ${c.a}33`,
                }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${c.a}1F`, border: `1px solid ${c.a}55`, color: c.a }}
                >
                  <c.i size={18} strokeWidth={2.2} />
                </div>
                <div className="text-white text-[15px] font-semibold leading-snug">
                  {c.t}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 5 — Why Studios & Licensors Should Care ============= */}
      <section
        id="why-care"
        className="relative py-24 md:py-32"
        data-testid="section-why-care"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12">
          <SectionHeader
            kicker="05 · Strategic Value"
            title="This Is Not Just Data. It Is IP Control."
            intro="When licensors gain SKU-level visibility, the consequences ripple across enforcement, royalty reconciliation, and consumer trust."
            kickerColor="#7AB2FF"
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { i: Eye, t: "Track licensed products in real time" },
              { i: AlertTriangle, t: "Identify unauthorized listings faster" },
              { i: ShieldCheck, t: "Reduce counterfeit exposure" },
              { i: BarChart3, t: "Improve royalty reconciliation" },
              { i: Map, t: "Protect territorial exclusivity" },
              { i: Scale, t: "Strengthen licensee compliance" },
              { i: Sparkles, t: "Improve consumer trust" },
              { i: FileText, t: "Better evidence for audits & enforcement" },
            ].map((b, i) => (
              <BenefitCard key={i} icon={b.i} title={b.t} delay={0.04 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 6 — AI Licensing Infrastructure ============= */}
      <section
        id="ai-infrastructure"
        className="relative py-24 md:py-32"
        data-testid="section-ai-infra"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12">
          <SectionHeader
            kicker="06 · Infrastructure"
            title="From Manual Licensing to AI-Powered Governance"
            intro="eGeez is building AI-powered infrastructure for the global licensing industry — connecting licensee verification, agreement parsing, SKU-level metadata, marketplace visibility, geofencing, and royalty traceability into a single intelligence layer."
            kickerColor="#22D3EE"
          />

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-16 relative rounded-3xl p-8 md:p-12 overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, rgba(13, 22, 60, 0.85) 0%, rgba(2, 6, 23, 0.95) 100%)",
              border: "1px solid rgba(34, 211, 238, 0.30)",
              boxShadow: "0 30px 100px -30px rgba(34, 211, 238, 0.25)",
            }}
          >
            <MatrixGrid opacity={0.18} density={10} />

            {/* Pipeline */}
            <div className="relative grid grid-cols-2 md:grid-cols-7 gap-6 md:gap-3 items-start">
              {[
                { l: "Licensor Approval", i: ScrollText },
                { l: "Verified Licensee", i: ShieldCheck },
                { l: "SKU Metadata", i: Database },
                { l: "Marketplace Verification", i: Search },
                { l: "Territory Monitoring", i: Map },
                { l: "Royalty Traceability", i: BarChart3 },
                { l: "Consumer Trust", i: Sparkles },
              ].map((step, i, arr) => (
                <div key={i} className="relative flex flex-col items-center">
                  <PipelineStep label={step.l} icon={step.i} idx={i} />
                  {/* Arrow connector (desktop only) */}
                  {i < arr.length - 1 && (
                    <div className="hidden md:block absolute top-[34px] -right-[55%] w-full pointer-events-none">
                      <motion.div
                        className="h-px"
                        style={{
                          background:
                            "linear-gradient(90deg, rgba(34,211,238,0.6) 0%, rgba(34,211,238,0.1) 100%)",
                        }}
                        initial={{ scaleX: 0, opacity: 0 }}
                        whileInView={{ scaleX: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4 + i * 0.1 }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============= SECTION 7 — Government & Enforcement ============= */}
      <section
        id="enforcement"
        className="relative py-24 md:py-32"
        data-testid="section-enforcement"
      >
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <SectionHeader
              kicker="07 · Public Policy"
              title="A New Layer for Customs, Governments, and Anti-Counterfeiting Policy"
              kickerColor="#FF6FA5"
            />
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-8 text-[16px] md:text-[18px] text-[#C9D5F2] leading-relaxed"
            >
              A centralized, SKU-level metadata system could help customs authorities,
              governments, and enforcement agencies authenticate licensed products,
              identify counterfeit goods at scale, and enforce digital-commerce
              compliance across jurisdictions.
            </motion.p>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-4 text-[16px] md:text-[18px] text-[#C9D5F2] leading-relaxed"
            >
              Combined with AI-driven verification, the licensing industry can move from
              reactive enforcement to{" "}
              <span className="text-white font-semibold">proactive deterrence</span>{" "}
              — protecting IP owners, retailers, marketplaces, and consumers in parallel.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative rounded-2xl p-7 overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, rgba(255, 111, 165, 0.10) 0%, rgba(13, 22, 60, 0.85) 100%)",
              border: "1px solid rgba(255, 111, 165, 0.35)",
            }}
          >
            <Network size={32} strokeWidth={1.8} className="text-[#FF6FA5] mb-4" />
            <div className="font-display text-white text-[22px] md:text-[26px] leading-tight">
              Authenticate. Enforce. Protect.
            </div>
            <div className="mt-3 text-[14px] text-[#C9D5F2] leading-snug">
              Three pillars of a new licensing intelligence stack — built for the global
              SKU economy.
            </div>
            <div className="mt-6 space-y-3">
              {[
                { l: "Customs · Border IP authentication", a: "#22D3EE" },
                { l: "Governments · Anti-counterfeit registries", a: "#FFD400" },
                { l: "Marketplaces · Verified listings layer", a: "#FF6FA5" },
              ].map((row, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: row.a, boxShadow: `0 0 8px ${row.a}` }}
                  />
                  <span className="text-[13px] md:text-[14px] text-white font-semibold">
                    {row.l}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============= SECTION 8 — Download Box ============= */}
      <section
        id="download"
        className="relative py-24 md:py-32"
        data-testid="section-download"
      >
        <div className="relative mx-auto max-w-[1100px] px-5 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[32px] overflow-hidden p-10 md:p-16 text-center"
            style={{
              background:
                "linear-gradient(160deg, rgba(0, 65, 230, 0.30) 0%, rgba(34, 211, 238, 0.18) 50%, rgba(13, 22, 60, 0.85) 100%)",
              border: "1px solid rgba(34, 211, 238, 0.45)",
              boxShadow:
                "0 0 80px -10px rgba(34, 211, 238, 0.30), inset 0 1px 0 rgba(255,255,255,0.05)",
            }}
          >
            <MatrixGrid opacity={0.2} density={14} />

            {/* Floating glow */}
            <motion.div
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -inset-10 bg-[#22D3EE]/20 blur-3xl rounded-full pointer-events-none"
              aria-hidden
            />

            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.4em] uppercase text-[#22D3EE] mb-5">
                <Sparkles size={12} />
                Full Research Paper
                <Sparkles size={12} />
              </div>

              <h2
                className="font-display text-white leading-[0.96] max-w-3xl mx-auto"
                style={{ fontSize: "clamp(34px, 5vw, 60px)" }}
              >
                Download the Full Research Paper
              </h2>

              <p className="mt-6 text-[15px] md:text-[17px] text-[#C9D5F2] leading-relaxed max-w-2xl mx-auto">
                Read Nathen Mazri&apos;s published <em>les Nouvelles</em> June 2026 paper
                on SKU-level licensing metadata, data fragmentation, AI infrastructure,
                territorial compliance, royalty governance, and the future of global
                licensing intelligence.
              </p>

              <a
                href={PAPER_PDF}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="mt-10 inline-flex items-center gap-3 px-9 py-5 rounded-full font-bold text-[16px] md:text-[17px] transition shadow-[0_25px_50px_-12px_rgba(34,211,238,0.60)]"
                style={{
                  background:
                    "linear-gradient(135deg, #22D3EE 0%, #0041E6 100%)",
                  color: "#031023",
                }}
                data-testid="download-pdf-btn"
              >
                <Download size={20} strokeWidth={2.5} />
                Download PDF
              </a>

              <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-[#7AB2FF]">
                <img
                  src={LES_LOGO}
                  alt=""
                  className="h-6 w-auto rounded-sm opacity-90"
                  draggable={false}
                />
                Published — les Nouvelles · June 2026
              </div>
            </div>
          </motion.div>

          {/* Citation footer */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 text-center text-[12px] md:text-[13px] text-[#7AB2FF] leading-relaxed"
          >
            Mazri, N. (2026). <em>Is Licensing Really a $369.9B Industry? Then Why Is
            It Still Measured by Surveys?</em> les Nouvelles, June 2026 issue.
            Licensing Executives Society International.
          </motion.div>
        </div>
      </section>

      {/* ============= ABOUT THE AUTHOR ============= */}
      <section
        id="about-author"
        className="relative pb-28 md:pb-36 pt-8"
        data-testid="section-about-author"
      >
        <div className="relative mx-auto max-w-[1100px] px-5 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[28px] overflow-hidden p-7 md:p-10"
            style={{
              background:
                "linear-gradient(160deg, rgba(13, 22, 60, 0.65) 0%, rgba(2, 6, 23, 0.85) 100%)",
              border: "1px solid rgba(34, 211, 238, 0.25)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              boxShadow:
                "0 0 60px -10px rgba(34, 211, 238, 0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            {/* subtle matrix glow */}
            <motion.div
              aria-hidden
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -inset-px rounded-[28px] pointer-events-none"
              style={{
                background:
                  "radial-gradient(500px circle at 15% 0%, rgba(34,211,238,0.15), transparent 45%)",
              }}
            />
            <MatrixGrid opacity={0.12} density={8} />

            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-7 md:gap-10 items-start">
              {/* LEFT — Photo + LinkedIn */}
              <div className="md:col-span-4 flex flex-col items-center md:items-start gap-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="relative"
                >
                  {/* Glow halo */}
                  <div
                    className="absolute -inset-3 rounded-2xl blur-2xl"
                    style={{ background: "rgba(34, 211, 238, 0.30)" }}
                    aria-hidden
                  />
                  <img
                    src={AUTHOR_PHOTO}
                    alt="Nathen Mazri — eGeez Chief Value Officer"
                    className="relative w-[200px] sm:w-[220px] md:w-full h-auto rounded-2xl object-cover ring-1 ring-[#22D3EE]/35 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
                    draggable={false}
                    data-testid="author-photo"
                  />
                  {/* Verified badge */}
                  <div
                    className="absolute -bottom-2 -right-2 flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black tracking-[0.2em] uppercase"
                    style={{
                      background: "linear-gradient(135deg, #22D3EE 0%, #0041E6 100%)",
                      color: "#031023",
                      boxShadow: "0 0 16px rgba(34,211,238,0.5)",
                    }}
                  >
                    <ShieldCheck size={11} strokeWidth={2.8} /> Verified Author
                  </div>
                </motion.div>

                {/* LinkedIn button — under the photo */}
                <a
                  href="https://www.linkedin.com/in/nathen-mazri/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition hover:translate-y-[-1px]"
                  style={{
                    background: "rgba(10, 102, 194, 0.18)",
                    border: "1px solid rgba(10, 102, 194, 0.55)",
                    color: "#7AB2FF",
                  }}
                  data-testid="author-linkedin"
                  aria-label="Nathen Mazri on LinkedIn"
                >
                  <Linkedin size={16} strokeWidth={2.2} fill="currentColor" />
                  <span className="text-[12px] font-bold tracking-[0.15em] uppercase">
                    LinkedIn
                  </span>
                </a>
              </div>

              {/* RIGHT — Bio + buttons */}
              <div className="md:col-span-8">
                <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.32em] uppercase text-[#22D3EE] mb-4">
                  <span className="inline-block w-6 h-px bg-[#22D3EE]" />
                  About the Author
                </div>

                <h3
                  className="font-display text-white leading-[0.95]"
                  style={{ fontSize: "clamp(28px, 3.4vw, 42px)" }}
                >
                  Nathen Mazri
                </h3>
                <div className="mt-1.5 text-[13px] md:text-[14px] font-semibold text-[#7AB2FF] tracking-wide">
                  Chief Value Officer · eGeez Inc.
                </div>

                <div className="mt-5 space-y-3.5 text-[14px] md:text-[15px] text-[#C9D5F2] leading-relaxed">
                  <p>
                    Nathen Mazri is a licensing executive, entrepreneur, researcher,
                    and AI infrastructure visionary with over{" "}
                    <span className="text-white font-semibold">15 years</span> of
                    experience across global licensing, branding, franchising, retail,
                    and intellectual property commercialization.
                  </p>
                  <p>
                    He is the Chief Value Officer of{" "}
                    <span className="text-white font-semibold">eGeez Inc.</span>, the
                    holding company behind eGeez Technologies and eGeez World, focused
                    on modernizing the global licensing industry through AI-powered
                    infrastructure, metadata intelligence, royalty transparency, and
                    territorial compliance.
                  </p>
                  <p>
                    A former entertainment licensee and licensing operator, Nathen has
                    worked with major rights holders and global brands while also
                    pioneering licensed IP real estate through his{" "}
                    <em>&ldquo;Entergagement&rdquo;</em> vision at eGeez World. He is
                    currently pursuing legal studies at the{" "}
                    <span className="text-white font-semibold">
                      University of London
                    </span>
                    . Nathen is the author of the published peer-reviewed research in{" "}
                    <em>les Nouvelles</em> by the Licensing Executives Society
                    International.
                  </p>
                  <p>
                    Over the past several years, he has invested into AI, software, and
                    data infrastructure to help solve one of licensing&apos;s largest
                    structural gaps:{" "}
                    <span className="text-white font-semibold">
                      the absence of unified licensing intelligence
                    </span>{" "}
                    addressing governments and IP owners.
                  </p>
                </div>

                {/* Buttons */}
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      label: "Visit eGeez",
                      sub: "egeez.com",
                      href: "https://www.egeez.com",
                      accent: "#22D3EE",
                      testid: "author-link-egeez",
                    },
                    {
                      label: "Visit eGeez World",
                      sub: "egeezworld.com",
                      href: "https://www.egeezworld.com",
                      accent: "#7AB2FF",
                      testid: "author-link-egeezworld",
                    },
                    {
                      label: "Licensing Executives Society",
                      sub: "lesi.org",
                      href: "https://www.lesi.org",
                      accent: "#FFD400",
                      testid: "author-link-lesi",
                    },
                  ].map((btn) => (
                    <a
                      key={btn.label}
                      href={btn.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition hover:translate-y-[-2px]"
                      style={{
                        background: "rgba(8, 14, 38, 0.55)",
                        borderColor: `${btn.accent}40`,
                      }}
                      data-testid={btn.testid}
                    >
                      <div>
                        <div className="text-white text-[13px] font-bold leading-tight">
                          {btn.label}
                        </div>
                        <div
                          className="text-[10px] font-bold tracking-[0.15em] uppercase mt-0.5"
                          style={{ color: btn.accent }}
                        >
                          {btn.sub}
                        </div>
                      </div>
                      <ExternalLink
                        size={14}
                        strokeWidth={2.4}
                        className="opacity-70 group-hover:opacity-100 transition"
                        style={{ color: btn.accent }}
                      />
                    </a>
                  ))}
                </div>

                {/* Micro tagline */}
                <div className="mt-5 text-[12px] md:text-[13px] italic text-[#7AB2FF] text-center md:text-left">
                  Building the infrastructure layer for the future of licensing.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
